import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { DasboardContext } from "../../context/dashboard-context";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import "./style.css";

const LIMIT = 50;

const V46VitalsLog = () => {
	const { sidebarOpen } = useContext(DasboardContext);
	const { deviceId } = useParams();

	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [skip, setSkip] = useState(0);

	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	const fetchLogs = async (append = false) => {
		setLoading(true);
		try {
			const params = new URLSearchParams({
				limit: String(LIMIT),
				skip: String(append ? skip : 0),
				dataType: "heartbeat,heartrate,temperature,bloodpressure",
			});
			if (startDate) params.set("startDate", startDate);
			if (endDate) params.set("endDate", endDate);

			const response = await fetch(
				`https://app.vivasmartcare.com/api/v46-logs/${deviceId}?${params}`
			);
			const result = await response.json();

			if (append) {
				setLogs((prev) => [...prev, ...result.data]);
				setSkip((prev) => prev + LIMIT);
			} else {
				setLogs(result.data);
				setSkip(LIMIT);
			}
			setTotalCount(result.totalCount);
			setHasMore(result.hasMore);
		} catch (error) {
			console.error("V46 logları alınırken hata:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (deviceId) {
			fetchLogs();
		}
	}, [deviceId, startDate, endDate]);

	const formatDate = (dateStr) => {
		if (!dateStr) return "-";
		const d = new Date(dateStr);
		return d.toLocaleString("tr-TR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		});
	};


	const getRow = (log) => {
		const v = log.vitals || {};
		const ds = log.deviceStatus || {};
		const a = log.activity || {};
		return {
			heartRate: v.heartRate || "-",
			bp: v.bloodPressureSystolic
				? `${v.bloodPressureSystolic}/${v.bloodPressureDiastolic}`
				: "-",
			spo2: v.bloodOxygen || "-",
			temp: v.bodyTemperature || "-",
			steps: a.steps != null ? a.steps : "-",
			battery: ds.battery != null ? `${ds.battery}%` : "-",
			triggeredBy: log.triggeredBy || "auto",
		};
	};

	const renderTriggerBadge = (triggeredBy) => {
		const map = {
			scheduled: { label: "Programlı", className: "badge-scheduled" },
			manual: { label: "Manuel", className: "badge-manual" },
			auto: { label: "Otomatik", className: "badge-auto" },
		};
		const { label, className } = map[triggeredBy] || map.auto;
		return <span className={`trigger-badge ${className}`}>{label}</span>;
	};

	const downloadPDF = () => {
		if (!logs || logs.length === 0) return;

		const doc = new jsPDF("l", "pt", "a4");
		doc.setFontSize(14);
		doc.text(`Cihaz Vital Raporu - ${deviceId}`, 40, 40);
		doc.setFontSize(9);
		doc.text(
			`Tarih: ${new Date().toLocaleString("tr-TR")} | Toplam: ${logs.length} kayit`,
			40,
			55
		);

		const tableColumn = [
			"Tarih/Saat",
			"Kaynak",
			"Kalp (bpm)",
			"Tansiyon",
			"SpO2 (%)",
			"Sicaklik (C)",
			"Adim",
			"Batarya",
		];

		const triggerLabel = {
			scheduled: "Programli",
			manual: "Manuel",
			auto: "Otomatik",
		};

		const tableRows = logs.map((log) => {
			const d = getRow(log);
			return [
				formatDate(log.createdAt),
				triggerLabel[d.triggeredBy] || "Otomatik",
				d.heartRate,
				d.bp,
				d.spo2,
				d.temp,
				d.steps,
				d.battery,
			];
		});

		autoTable(doc, {
			head: [tableColumn],
			body: tableRows,
			startY: 70,
			styles: { fontSize: 8 },
			headStyles: { fillColor: [22, 160, 133] },
		});

		doc.save(`v46-vital-rapor-${deviceId}.pdf`);
	};

	return (
		<>
			<Breadcrumb />
			<div
				className={`v46-vitals-page component-wrapper ${
					sidebarOpen ? "" : "close"
				}`}
			>
				<div className="v46-vitals-header">
					<h5>Cihaz Vital Logları - {deviceId}</h5>
					<div className="v46-vitals-actions">
						<span className="v46-vitals-count">
							{totalCount} kayıt
						</span>
						<Button
							variant="success"
							size="sm"
							onClick={downloadPDF}
							disabled={logs.length === 0}
						>
							PDF İndir
						</Button>
					</div>
				</div>

				<div className="v46-vitals-filters">
					<Form.Control
						type="datetime-local"
						size="sm"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						className="v46-filter-date"
					/>
					<Form.Control
						type="datetime-local"
						size="sm"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						className="v46-filter-date"
					/>
					{(startDate || endDate) && (
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={() => {
								setStartDate("");
								setEndDate("");
							}}
						>
							Temizle
						</Button>
					)}
				</div>

				<div className="v46-vitals-table-wrapper">
					<Table striped bordered hover size="sm" responsive>
						<thead>
							<tr>
								<th>#</th>
								<th>Tarih/Saat</th>
								<th>Kaynak</th>
								<th>Kalp (bpm)</th>
								<th>Tansiyon</th>
								<th>SpO2 (%)</th>
								<th>Sıcaklık (°C)</th>
								<th>Adım</th>
								<th>Batarya</th>
							</tr>
						</thead>
						<tbody>
							{logs.map((log, index) => {
								const d = getRow(log);
								return (
									<tr key={log._id || index}>
										<td>{index + 1}</td>
										<td>{formatDate(log.createdAt)}</td>
										<td>{renderTriggerBadge(d.triggeredBy)}</td>
										<td>{d.heartRate}</td>
										<td>{d.bp}</td>
										<td>{d.spo2}</td>
										<td>{d.temp}</td>
										<td>{d.steps}</td>
										<td>{d.battery}</td>
									</tr>
								);
							})}
							{logs.length === 0 && !loading && (
								<tr>
									<td colSpan={9} className="text-center text-muted">
										Kayıt bulunamadı
									</td>
								</tr>
							)}
						</tbody>
					</Table>
				</div>

				{loading && (
					<div className="text-center py-3">
						<span
							className="spinner-border spinner-border-sm me-2"
							role="status"
						></span>
						Yükleniyor...
					</div>
				)}

				{hasMore && !loading && (
					<div className="text-center py-3">
						<Button
							variant="outline-primary"
							size="sm"
							onClick={() => fetchLogs(true)}
						>
							Daha Fazla Yükle ({logs.length} / {totalCount})
						</Button>
					</div>
				)}
			</div>
		</>
	);
};

export default V46VitalsLog;
